import { DateTime } from "luxon";
import {
  Button,
  Pill,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import { SiteSurveyAppointment } from "../../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { Card, CardContent } from "../Card";
import { HEADER_DATE_FORMAT_LONG, SLOT_TIME_FORMAT } from "./constants";
import {
  ConfirmationCard,
  ScheduledAppointmentCTA,
  ScheduledInfoPillWrapper,
} from "./siteSurveySchedulingStyles";

type ScheduledStatusPill = {
  type: "info" | "success" | "warning" | "error";
  text: string;
};

type ConfirmationRefreshAction = {
  show: boolean;
  isLoading: boolean;
  onRefresh: () => void;
  testId?: string;
};

type SiteSurveyScheduledCardProps = {
  appointment: SiteSurveyAppointment;
  timeZoneId?: string;
  maxWidth?: string;
  statusPill?: ScheduledStatusPill;
  onRequestReschedule: () => void;
  onRequestCancel?: () => void;
  scheduledCardTestId?: string;
  rescheduleButtonTestId?: string;
  cancelButtonTestId?: string;
  showCancelButton?: boolean;
  showRescheduleButton?: boolean;
  confirmationRefresh?: ConfirmationRefreshAction;
  borderRadius?: string;
  showStartTimeOnly?: boolean;
  scheduledArrivalIntro?: string;
};

const SiteSurveyScheduledCard: React.FC<SiteSurveyScheduledCardProps> = ({
  appointment,
  timeZoneId,
  maxWidth,
  statusPill,
  onRequestReschedule,
  onRequestCancel,
  scheduledCardTestId,
  rescheduleButtonTestId,
  cancelButtonTestId,
  showCancelButton = true,
  showRescheduleButton = true,
  confirmationRefresh,
  borderRadius,
  showStartTimeOnly = false,
  scheduledArrivalIntro,
}) => {
  const { arrivalStartDate, arrivalEndDate } = appointment;
  const formattedStartDate = DateTime.fromISO(arrivalStartDate).toLocaleString({
    ...SLOT_TIME_FORMAT,
    timeZone: timeZoneId,
  });
  const formattedEndDate = DateTime.fromISO(arrivalEndDate).toLocaleString({
    ...SLOT_TIME_FORMAT,
    timeZone: timeZoneId,
  });
  const arrivalIntro =
    scheduledArrivalIntro ??
    (showStartTimeOnly
      ? "You have scheduled your appointment. A site technician will arrive at:"
      : "You have scheduled your appointment. A site technician will arrive between:");
  const formattedArrivalTime = showStartTimeOnly
    ? formattedStartDate
    : `${formattedStartDate} - ${formattedEndDate}`;

  return (
    <ConfirmationCard
      $maxWidth={maxWidth}
      {...(scheduledCardTestId ? { "data-testid": scheduledCardTestId } : {})}
    >
      <Card borderRadius={borderRadius}>
        <CardContent>
          {statusPill ? (
            <ScheduledInfoPillWrapper>
              <Pill type={statusPill.type} text={statusPill.text} />
            </ScheduledInfoPillWrapper>
          ) : null}
          <Typography>{arrivalIntro}</Typography>
          <Typography fontWeight="medium" size={tokens.WEB_BODY_LG_FONT_SIZE}>
            {formattedArrivalTime}
          </Typography>
          <Typography fontWeight="medium" size={tokens.WEB_BODY_LG_FONT_SIZE}>
            {DateTime.fromISO(arrivalStartDate).toLocaleString({
              ...HEADER_DATE_FORMAT_LONG,
              timeZone: timeZoneId,
            })}
          </Typography>
          {(showRescheduleButton ||
            showCancelButton ||
            confirmationRefresh?.show) && (
            <ScheduledAppointmentCTA>
              {confirmationRefresh?.show && (
                <Button
                  size="xs"
                  color="secondary"
                  isLoading={confirmationRefresh.isLoading}
                  onClick={confirmationRefresh.onRefresh}
                  data-testid={confirmationRefresh.testId}
                >
                  Refresh
                </Button>
              )}
              {showRescheduleButton && (
                <Button
                  size="xs"
                  color="secondary"
                  onClick={onRequestReschedule}
                  data-testid={rescheduleButtonTestId}
                >
                  Reschedule
                </Button>
              )}
              {showCancelButton && (
                <Button
                  size="xs"
                  color="link"
                  onClick={onRequestCancel}
                  data-testid={cancelButtonTestId}
                >
                  Cancel
                </Button>
              )}
            </ScheduledAppointmentCTA>
          )}
        </CardContent>
      </Card>
    </ConfirmationCard>
  );
};

export { SiteSurveyScheduledCard };
export type {
  ConfirmationRefreshAction,
  ScheduledStatusPill,
  SiteSurveyScheduledCardProps,
};
